import { Controller } from '@hotwired/stimulus';
import { readConditions } from '../lib/conditions';
import { paint } from '../lib/dom';
import { credentialLabel } from '../lib/workflow/presenter';

const DOCUMENT_PATH = 'workflow.json';
const PARSE_DELAY = 500;
const CONCURRENCY_MODES = ['off', 'queue', 'cancel'];
const TRIGGER_DEFAULTS = { name: '', webhookId: '', events: '', conditions: '', cron: '' };
const STEP_MODES = ['script', 'agent', 'gate', 'workflow'];

export default class extends Controller {
  static targets = ['monaco', 'status'];

  static classes = ['statusInvalid', 'statusValid'];

  static values = {
    bundles: Array,
    credentials: Array,
    hint: String,
    messages: Object,
    webhooks: Array,
    workflows: Array,
  };

  disconnect() {
    clearTimeout(this.parseHandle);
  }

  show(workflow) {
    this.current = workflow;

    this.#monaco?.forget(DOCUMENT_PATH);
    this.#monaco?.open(DOCUMENT_PATH, this.#serialize(workflow), 'json');
    this.#monaco?.refresh();
  }

  sync(workflow) {
    this.current = workflow;
  }

  showStatus(error) {
    const invalid = Boolean(error);

    paint(this.statusTarget, invalid, this.statusInvalidClasses, this.statusValidClasses);
    this.#reveal(this.#role('icon-valid'), !invalid);
    this.#reveal(this.#role('icon-invalid'), invalid);
    this.#role('message').textContent = error ?? this.hintValue;
  }

  cancelPending() {
    clearTimeout(this.parseHandle);
  }

  editorReady() {
    if (this.current) this.show(this.current);
  }

  documentChanged({ detail: { content } }) {
    if (this.element.hidden) return;

    clearTimeout(this.parseHandle);

    this.parseHandle = setTimeout(() => this.dispatch('parsed', { detail: this.#parse(content) }), PARSE_DELAY);
  }

  get #monaco() {
    return this.application.getControllerForElementAndIdentifier(this.monacoTarget, 'monaco');
  }

  #serialize(workflow) {
    const { concurrency, steps, triggers } = workflow;
    const document = { name: workflow.name, description: workflow.description };

    if (concurrency.mode !== 'off') {
      document.concurrency = concurrency.key
        ? { mode: concurrency.mode, key: concurrency.key }
        : { mode: concurrency.mode };
    }

    document.on = triggers.map((trigger) => this.#serializeTrigger(trigger, workflow));
    document.steps = steps.map((step) => this.#serializeStep(step, workflow));

    return JSON.stringify(document, null, 2);
  }

  #serializeTrigger(trigger, workflow) {
    const name = trigger.name.trim();
    const listening = name ? { name } : {};

    if (trigger.kind === 'cron') {
      listening.cron = trigger.cron;
    } else {
      listening.webhook = this.#webhookSlug(trigger.webhookId);

      const events = this.#splitEvents(trigger.events);

      if (events.length > 0) listening.events = events;

      const conditions = readConditions(trigger.conditions);

      if (conditions && Object.keys(conditions).length > 0) listening.conditions = conditions;
    }

    listening.steps = trigger.starts
      .map((id) => workflow.steps.find((candidate) => candidate.id === id)?.key)
      .filter(Boolean);

    return listening;
  }

  #serializeStep(step, workflow) {
    const common = { key: step.key, name: step.name, mode: step.mode };

    if (step.mode !== 'workflow') common.bundles = this.#serializeBundles(step);

    const depends = step.dependsOn
      .map((id) => workflow.steps.find((candidate) => candidate.id === id)?.key)
      .filter(Boolean);

    if (depends.length > 0) common.depends_on = depends;

    const conditions = readConditions(step.conditions);

    if (conditions && Object.keys(conditions).length > 0) common.conditions = conditions;

    if (step.mode === 'script') return { ...common, command: step.command };
    if (step.mode === 'gate') return { ...common, instructions: step.instructions ?? '' };
    if (step.mode === 'workflow') return this.#serializeWorkflowStep(common, step);

    const agent = {
      ...common,
      prompt: step.prompt,
      interruptible: Boolean(step.interruptible),
    };
    const credential = credentialLabel(step.credentialId, this.credentialsValue);

    if (credential) agent.credential = credential;

    return agent;
  }

  #serializeWorkflowStep(common, step) {
    const serialized = {
      ...common,
      workflow: this.#workflowName(step.childWorkflowId),
      inputs: this.#readObject(step.childInputs) ?? {},
    };

    if (step.each) serialized.each = step.each;
    if (step.allowFailures) serialized.allow_failures = true;

    return serialized;
  }

  #readObject(text) {
    try {
      const value = JSON.parse(text);

      return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
    } catch {
      return null;
    }
  }

  #credentialId(label) {
    const ids = this.credentialsValue.map((candidate) => candidate.id);

    return ids.find((id) => credentialLabel(id, this.credentialsValue) === label) ?? '';
  }

  #workflowName(id) {
    return this.workflowsValue.find((candidate) => candidate.id === id)?.name ?? '';
  }

  #workflowId(name) {
    return this.workflowsValue.find((candidate) => candidate.name === name)?.id ?? '';
  }

  #splitEvents(events) {
    return events
      .split(',')
      .map((event) => event.trim())
      .filter((event) => event.length > 0);
  }

  #serializeBundles(step) {
    return step.bundles
      .map((attachment) => this.#serializeBundle(attachment))
      .filter(Boolean);
  }

  #serializeBundle(attachment) {
    const name = this.bundlesValue.find((bundle) => bundle.id === attachment.bundleId)?.name;

    if (!name) return null;
    if (attachment.revisionNumber === null || attachment.revisionNumber === undefined) return name;

    return { bundle: name, revision: attachment.revisionNumber };
  }

  #readBundle(item) {
    if (typeof item === 'string') return { name: item, revision: null };

    const revision = Number(item?.revision);

    return { name: String(item?.bundle ?? ''), revision: Number.isInteger(revision) ? revision : null };
  }

  #bundleId(name) {
    return this.bundlesValue.find((bundle) => bundle.name === name)?.id ?? '';
  }

  #webhookSlug(id) {
    return this.webhooksValue.find((webhook) => webhook.id === id)?.slug ?? '';
  }

  #parse(text) {
    let data;

    try {
      data = JSON.parse(text);
    } catch (error) {
      return { error: this.messagesValue.documentUnparsable.replace('%{message}', error.message) };
    }

    const shapeless = !data || typeof data !== 'object' || Array.isArray(data);

    if (shapeless) return { error: this.messagesValue.documentNotAnObject };
    if (!Array.isArray(data.steps)) return { error: this.messagesValue.documentStepsNotAnArray };
    if (data.on !== undefined && !Array.isArray(data.on)) {
      return { error: this.messagesValue.documentTriggersNotAnArray };
    }

    const concurrency = this.#parseConcurrency(data.concurrency);

    if (concurrency.error) return concurrency;

    const triggers = [];

    for (const [index, raw] of (data.on ?? []).entries()) {
      const trigger = this.#parseTrigger(raw, index);

      if (trigger.error) return trigger;

      triggers.push(trigger.trigger);
    }

    const steps = [];

    for (const [index, raw] of data.steps.entries()) {
      const step = this.#parseStep(raw, index);

      if (step.error) return step;

      steps.push(step.step);
    }

    const keys = steps.map((step) => step.key);

    if (new Set(keys).size !== keys.length) return { error: this.messagesValue.stepKeyDuplicated };

    for (const [index, step] of steps.entries()) {
      const unknown = step.dependsOnKeys.find((key) => !keys.includes(key));

      if (unknown) {
        const message = this.messagesValue.documentUnknownDependency.replace('%{index}', index + 1);

        return { error: message.replace('%{key}', unknown) };
      }
    }

    for (const [index, trigger] of triggers.entries()) {
      const unknown = trigger.stepKeys.find((key) => !keys.includes(key));

      if (unknown) {
        const message = this.messagesValue.documentUnknownTriggerStep.replace('%{index}', index + 1);

        return { error: message.replace('%{key}', unknown) };
      }
    }

    return {
      workflow: {
        name: String(data.name ?? '').trim(),
        description: String(data.description ?? ''),
        concurrency: concurrency.value,
        triggers,
        steps,
        variables: this.current.variables,
        inputs: this.current.inputs,
      },
    };
  }

  #parseConcurrency(raw) {
    if (raw === undefined || raw === null) return { value: { mode: 'off', key: '' } };

    if (typeof raw !== 'object' || Array.isArray(raw)) {
      return { error: this.messagesValue.documentConcurrencyNotAnObject };
    }

    const mode = raw.mode ?? 'off';

    if (!CONCURRENCY_MODES.includes(mode)) {
      return { error: this.messagesValue.documentUnknownConcurrencyMode.replace('%{mode}', mode) };
    }

    return { value: { mode, key: String(raw.key ?? '') } };
  }

  #parseStep(raw, index) {
    if (!STEP_MODES.includes(raw.mode)) {
      const message = this.messagesValue.documentUnknownMode.replace('%{index}', index + 1);

      return { error: message.replace('%{mode}', raw.mode) };
    }

    const childWorkflowId = raw.mode === 'workflow' ? this.#workflowId(String(raw.workflow ?? '')) : '';

    if (raw.mode === 'workflow' && !childWorkflowId) {
      const message = this.messagesValue.documentUnknownWorkflow.replace('%{index}', index + 1);

      return { error: message.replace('%{name}', String(raw.workflow ?? '')) };
    }

    const declared = Array.isArray(raw.bundles) ? raw.bundles.map((item) => this.#readBundle(item)) : [];
    const unknown = declared.find((item) => !this.bundlesValue.some((candidate) => candidate.name === item.name));

    if (unknown) {
      const message = this.messagesValue.documentUnknownBundle.replace('%{index}', index + 1);

      return { error: message.replace('%{name}', unknown.name) };
    }

    const key = String(raw.key ?? '');
    const known = key ? this.current.steps.find((step) => step.key === key) : null;
    const blankInputs = raw.inputs === undefined || raw.inputs === null || JSON.stringify(raw.inputs) === '{}';

    return {
      step: {
        id: known ? known.id : null,
        key: key,
        name: String(raw.name ?? ''),
        mode: raw.mode,
        bundles: declared.map((item) => ({
          bundleId: this.#bundleId(item.name),
          revisionNumber: item.revision,
        })),
        command: String(raw.command ?? ''),
        credentialId: this.#credentialId(String(raw.credential ?? '')),
        prompt: String(raw.prompt ?? ''),
        instructions: String(raw.instructions ?? ''),
        interruptible: Boolean(raw.interruptible),
        childWorkflowId,
        childInputs: blankInputs ? '' : JSON.stringify(raw.inputs, null, 2),
        each: String(raw.each ?? ''),
        allowFailures: Boolean(raw.allow_failures),
        conditions: raw.conditions ? JSON.stringify(raw.conditions, null, 2) : '',
        canvasX: known?.canvasX ?? null,
        canvasY: known?.canvasY ?? null,
        dependsOnKeys: Array.isArray(raw.depends_on) ? raw.depends_on.map(String) : [],
      },
    };
  }

  #parseTrigger(raw, index) {
    const name = raw?.name ? String(raw.name) : '';

    const stepKeys = Array.isArray(raw?.steps) ? raw.steps.map(String) : [];

    if (raw?.cron) {
      return { trigger: { id: null, kind: 'cron', ...TRIGGER_DEFAULTS, name, stepKeys, cron: String(raw.cron) } };
    }

    if (!raw?.webhook) {
      return { error: this.messagesValue.documentTriggerUnreadable.replace('%{index}', index + 1) };
    }

    const webhook = this.webhooksValue.find((candidate) => candidate.slug === raw.webhook);

    if (!webhook) {
      return { error: this.messagesValue.documentUnknownWebhook.replace('%{slug}', raw.webhook) };
    }

    return {
      trigger: {
        id: null,
        kind: 'webhook',
        ...TRIGGER_DEFAULTS,
        name,
        stepKeys,
        webhookId: webhook.id,
        events: Array.isArray(raw.events) ? raw.events.join(', ') : '',
        conditions: raw.conditions ? JSON.stringify(raw.conditions, null, 2) : '',
      },
    };
  }

  #reveal(node, visible) {
    node.classList.toggle('hidden', !visible);
  }

  #role(role) {
    return this.statusTarget.querySelector(`[data-role="${role}"]`);
  }
}
